import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface UserTabCounts {
  hasDisplay: boolean;
  sequenceCount: number;
  songCount: number;
}

interface UseUserTabCountsReturn {
  counts: UserTabCounts;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useUserTabCounts(userId?: string): UseUserTabCountsReturn {
  const [counts, setCounts] = useState<UserTabCounts>({
    hasDisplay: false,
    sequenceCount: 0,
    songCount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCounts = async () => {
    if (!userId) {
      setCounts({ hasDisplay: false, sequenceCount: 0, songCount: 0 });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch all counts in parallel for better performance
      const [displayResult, sequenceResult, songResult] = await Promise.all([
        // Check if user has any displays
        supabase
          .from('displays')
          .select('id')
          .eq('user_id', userId)
          .limit(1),
        
        // Count sequences by this user
        supabase
          .from('sequences')
          .select('id')
          .eq('user_id', userId),
        
        // Count songs: get display years first, then songs
        (async () => {
          const { data: displayYears } = await supabase
            .from('display_years')
            .select('id')
            .eq('user_id', userId);
          
          if (!displayYears || displayYears.length === 0) {
            return { data: [], error: null };
          }
          
          const displayYearIds = displayYears.map(dy => dy.id);
          return supabase
            .from('display_songs')
            .select('id')
            .in('display_year_id', displayYearIds);
        })()
      ]);

      if (displayResult.error) {
        throw new Error(`Display count error: ${displayResult.error.message}`);
      }
      if (sequenceResult.error) {
        throw new Error(`Sequence count error: ${sequenceResult.error.message}`);
      }
      if (songResult.error) {
        throw new Error(`Song count error: ${songResult.error.message}`);
      }

      setCounts({
        hasDisplay: (displayResult.data?.length || 0) > 0,
        sequenceCount: sequenceResult.data?.length || 0,
        songCount: songResult.data?.length || 0,
      });
    } catch (err) {
      console.error('Error fetching user tab counts:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch counts');
      setCounts({ hasDisplay: false, sequenceCount: 0, songCount: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();
  }, [userId]);

  const refetch = async () => {
    await fetchCounts();
  };

  return {
    counts,
    isLoading,
    error,
    refetch,
  };
}